import { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { X, Upload, FileText, Check, AlertCircle, Globe, UserPlus, User, Baby, Eye, EyeOff } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

export default function PDFUploadModal({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [formType, setFormType] = useState("consent");
  const [category, setCategory] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [appliesToAll, setAppliesToAll] = useState(false);
  const [appliesToNew, setAppliesToNew] = useState(false);
  const [appliesToReturning, setAppliesToReturning] = useState(false);
  const [appliesToUnder18, setAppliesToUnder18] = useState(false);
  const [assignedApptTypes, setAssignedApptTypes] = useState([]);
  const [acknowledgementText, setAcknowledgementText] = useState("I confirm that I have read and understood this document.");
  const [consentText, setConsentText] = useState("");
  const [requiresSignature, setRequiresSignature] = useState(true);
  const [apptTypes, setApptTypes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Load appointment types
  useEffect(() => {
    api.get("/appointment-types").then(res => setApptTypes(res.data.appointment_types || [])).catch(() => {});
  }, []);

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    
    // Validate file type
    if (selectedFile.type !== "application/pdf") {
      setError("Please select a PDF file");
      return;
    }
    
    // Validate file size (25MB)
    if (selectedFile.size > 25 * 1024 * 1024) {
      setError("File size must be less than 25MB");
      return;
    }
    
    setError("");
    setFile(selectedFile);
    
    // Auto-populate name from filename if empty
    if (!name) {
      const baseName = selectedFile.name.replace(/\.pdf$/i, "").replace(/[-_]/g, " ");
      setName(baseName.charAt(0).toUpperCase() + baseName.slice(1));
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const toggleApptType = (typeId) => {
    setAssignedApptTypes(prev =>
      prev.includes(typeId)
        ? prev.filter(id => id !== typeId)
        : [...prev, typeId]
    );
  };

  const handleUpload = async () => {
    if (!file) {
      setError("Please select a PDF file");
      return;
    }
    if (!name.trim()) {
      setError("Please enter a form name");
      return;
    }

    setUploading(true);
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("name", name);
      formData.append("form_type", formType);
      formData.append("description", description);
      formData.append("category", category);
      formData.append("is_active", isActive);
      formData.append("applies_to_all", appliesToAll);
      formData.append("applies_to_new_patients", appliesToNew);
      formData.append("applies_to_returning_patients", appliesToReturning);
      formData.append("applies_to_under_18", appliesToUnder18);
      formData.append("assigned_appointment_type_ids", JSON.stringify(assignedApptTypes));
      formData.append("acknowledgement_text", acknowledgementText);
      formData.append("consent_text", consentText);
      formData.append("requires_signature", requiresSignature);

      const { data } = await api.post("/forms/upload-pdf", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      onSuccess?.(data.template);
    } catch (err) {
      const msg = err.response?.data?.detail || "Failed to upload PDF. Please try again.";
      setError(msg);
    }
    setUploading(false);
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center p-4" data-testid="pdf-upload-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[#1A1B1A]/[0.06] flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#205B5A]/10 flex items-center justify-center">
              <Upload className="w-4 h-4 text-[#205B5A]" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-[#1A1B1A]" style={H}>Upload PDF Form</h2>
              <p className="text-xs text-[#757675]">Upload an existing clinic form as PDF</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-[#757675] hover:text-[#1A1B1A] rounded-lg hover:bg-[#FAF9F6]">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Error banner */}
          {error && (
            <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 flex items-center gap-2" data-testid="upload-error">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* File drop zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              dragOver
                ? "border-[#205B5A] bg-[#205B5A]/5"
                : file
                ? "border-[#205B5A]/30 bg-[#205B5A]/5"
                : "border-[#1A1B1A]/15 hover:border-[#205B5A]/50 hover:bg-[#FAF9F6]"
            }`}
            data-testid="pdf-drop-zone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) => handleFileSelect(e.target.files?.[0])}
              className="hidden"
              data-testid="pdf-file-input"
            />
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#205B5A]/10 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#205B5A]" />
                </div>
                <div className="text-left">
                  <p className="text-sm font-medium text-[#1A1B1A]">{file.name}</p>
                  <p className="text-xs text-[#757675]">{formatFileSize(file.size)}</p>
                </div>
                <button
                  onClick={(e) => { e.stopPropagation(); setFile(null); }}
                  className="ml-2 p-1 text-[#757675] hover:text-red-500 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <Upload className="w-8 h-8 text-[#757675]/50 mx-auto mb-3" />
                <p className="text-sm text-[#757675] mb-1">Drop your PDF here or click to browse</p>
                <p className="text-xs text-[#757675]/70">Maximum file size: 25MB</p>
              </>
            )}
          </div>

          {/* Form details */}
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-[#1A1B1A] mb-1.5 block">Form name *</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Patient Consent Form"
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]"
                data-testid="pdf-form-name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium text-[#1A1B1A] mb-1.5 block">Form type</label>
                <select
                  value={formType}
                  onChange={(e) => setFormType(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]"
                  data-testid="pdf-form-type"
                >
                  <option value="consent">Consent</option>
                  <option value="intake">Intake</option>
                  <option value="treatment_info">Treatment Info</option>
                  <option value="privacy">Privacy</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-[#1A1B1A] mb-1.5 block">Category (optional)</label>
                <input
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="e.g. General, Osteopathy"
                  className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-[#1A1B1A] mb-1.5 block">Description (optional)</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                placeholder="Brief description of the form..."
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A] resize-none"
              />
            </div>
          </div>

          {/* Status Toggle */}
          <div>
            <label className="flex items-center justify-between p-3 rounded-xl border border-[#1A1B1A]/[0.06] cursor-pointer hover:bg-[#FAF9F6]">
              <div className="flex items-center gap-3">
                {isActive ? <Eye className="w-4 h-4 text-[#205B5A]" /> : <EyeOff className="w-4 h-4 text-[#757675]" />}
                <div>
                  <p className="text-sm font-medium text-[#1A1B1A]">Form Status</p>
                  <p className="text-xs text-[#757675]">{isActive ? "Active - can be assigned" : "Inactive"}</p>
                </div>
              </div>
              <div
                className={`w-10 h-6 rounded-full relative transition-colors ${isActive ? "bg-[#205B5A]" : "bg-[#1A1B1A]/10"}`}
                onClick={() => setIsActive(!isActive)}
              >
                <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-transform ${isActive ? "translate-x-5" : "translate-x-1"}`} />
              </div>
            </label>
          </div>

          {/* Assignment Rules */}
          <div>
            <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">Assignment Rules</h3>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 rounded-xl border border-[#1A1B1A]/[0.06] cursor-pointer hover:bg-[#FAF9F6]">
                <input type="checkbox" checked={appliesToAll} onChange={(e) => setAppliesToAll(e.target.checked)}
                  className="w-4 h-4 rounded border-[#1A1B1A]/20 text-[#205B5A]" />
                <div className="flex items-center gap-2 flex-1">
                  <Globe className="w-4 h-4 text-[#757675]" />
                  <div>
                    <p className="text-sm font-medium text-[#1A1B1A]">All appointments</p>
                    <p className="text-xs text-[#757675]">Required for every appointment</p>
                  </div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 rounded-xl border border-[#1A1B1A]/[0.06] cursor-pointer hover:bg-[#FAF9F6]">
                <input type="checkbox" checked={appliesToNew} onChange={(e) => setAppliesToNew(e.target.checked)}
                  className="w-4 h-4 rounded border-[#1A1B1A]/20 text-[#205B5A]" />
                <div className="flex items-center gap-2 flex-1">
                  <UserPlus className="w-4 h-4 text-[#757675]" />
                  <div>
                    <p className="text-sm font-medium text-[#1A1B1A]">New patients</p>
                    <p className="text-xs text-[#757675]">Required for first-time patients</p>
                  </div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 rounded-xl border border-[#1A1B1A]/[0.06] cursor-pointer hover:bg-[#FAF9F6]">
                <input type="checkbox" checked={appliesToReturning} onChange={(e) => setAppliesToReturning(e.target.checked)}
                  className="w-4 h-4 rounded border-[#1A1B1A]/20 text-[#205B5A]" />
                <div className="flex items-center gap-2 flex-1">
                  <User className="w-4 h-4 text-[#757675]" />
                  <div>
                    <p className="text-sm font-medium text-[#1A1B1A]">Returning patients</p>
                    <p className="text-xs text-[#757675]">Required for patients with previous visits</p>
                  </div>
                </div>
              </label>

              <label className="flex items-center gap-3 p-3 rounded-xl border border-[#1A1B1A]/[0.06] cursor-pointer hover:bg-[#FAF9F6]">
                <input type="checkbox" checked={appliesToUnder18} onChange={(e) => setAppliesToUnder18(e.target.checked)}
                  className="w-4 h-4 rounded border-[#1A1B1A]/20 text-[#205B5A]" />
                <div className="flex items-center gap-2 flex-1">
                  <Baby className="w-4 h-4 text-[#757675]" />
                  <div>
                    <p className="text-sm font-medium text-[#1A1B1A]">Under 18</p>
                    <p className="text-xs text-[#757675]">Required for minor patients</p>
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* Appointment Types */}
          {apptTypes.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider mb-3">Specific Appointment Types</h3>
              <div className="space-y-1.5 max-h-36 overflow-y-auto border border-[#1A1B1A]/[0.06] rounded-xl p-2">
                {apptTypes.map(at => (
                  <label key={at.id} className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-[#FAF9F6]">
                    <input
                      type="checkbox"
                      checked={assignedApptTypes.includes(at.id)}
                      onChange={() => toggleApptType(at.id)}
                      className="w-4 h-4 rounded border-[#1A1B1A]/20 text-[#205B5A]"
                    />
                    <div className="flex items-center gap-2 flex-1">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: at.color || "#205B5A" }} />
                      <span className="text-sm text-[#1A1B1A]">{at.name}</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Acknowledgement & Consent Text */}
          <div className="space-y-4">
            <h3 className="text-xs font-semibold text-[#757675] uppercase tracking-wider">Patient Acknowledgement</h3>
            
            <div>
              <label className="text-xs font-medium text-[#1A1B1A] mb-1.5 block">Acknowledgement text *</label>
              <input
                value={acknowledgementText}
                onChange={(e) => setAcknowledgementText(e.target.value)}
                placeholder="I confirm that I have read and understood this document."
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]"
                data-testid="acknowledgement-text"
              />
              <p className="text-[10px] text-[#757675] mt-1">Patient must check this before submitting</p>
            </div>

            <div>
              <label className="text-xs font-medium text-[#1A1B1A] mb-1.5 block">Consent text (optional)</label>
              <input
                value={consentText}
                onChange={(e) => setConsentText(e.target.value)}
                placeholder="I consent to the treatment described in this document."
                className="w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm focus:outline-none focus:border-[#205B5A]"
                data-testid="consent-text"
              />
              <p className="text-[10px] text-[#757675] mt-1">Additional consent checkbox (leave empty to skip)</p>
            </div>

            <label className="flex items-center gap-3 p-3 rounded-xl border border-[#1A1B1A]/[0.06] cursor-pointer hover:bg-[#FAF9F6]">
              <input
                type="checkbox"
                checked={requiresSignature}
                onChange={(e) => setRequiresSignature(e.target.checked)}
                className="w-4 h-4 rounded border-[#1A1B1A]/20 text-[#205B5A]"
              />
              <div>
                <p className="text-sm font-medium text-[#1A1B1A]">Require signature</p>
                <p className="text-xs text-[#757675]">Patient must sign before submitting</p>
              </div>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#1A1B1A]/[0.06] flex items-center justify-end gap-3 flex-shrink-0 bg-[#FAF9F6]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-[#757675] hover:text-[#1A1B1A]"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={uploading || !file || !name.trim()}
            className="bg-[#205B5A] text-white px-5 py-2 rounded-full text-sm font-medium hover:bg-[#174544] disabled:opacity-40 flex items-center gap-2"
            data-testid="upload-pdf-submit"
          >
            {uploading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload form
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
